import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { libraryApi } from '@/services/libraryApi';
import type { Library } from '@/types/catalog';

/**
 * The libraries the signed-in learner can access (contract-based), as returned
 * by GET /api/me/libraries. Drives the Content Library navigation fan-out and
 * the browse-by-library page's slug → id resolution.
 */
export const useAccessibleLibraryStore = defineStore('accessibleLibrary', () => {
  const libraries = ref<Library[]>([]);
  const loading = ref(false);
  const loaded = ref(false);
  const error = ref<string | null>(null);

  const byId = computed<Record<number, Library>>(() => {
    const map: Record<number, Library> = {};
    for (const l of libraries.value) {
      map[l.id] = l;
    }
    return map;
  });

  function getById(id: number | null | undefined): Library | undefined {
    if (id == null) return undefined;
    return byId.value[id];
  }

  /** The variant kinds the learner can reach through their accessible libraries. */
  const accessibleKinds = computed<Set<string>>(() => {
    const kinds = new Set<string>();
    for (const l of libraries.value) {
      if (l.kind) kinds.add(l.kind);
    }
    return kinds;
  });

  /**
   * The first of a course's variant kinds the learner has access to — the
   * default lens for course links on mixed surfaces (Search Results, skills /
   * practice-area listings). Deterministic: variantKinds arrives in the
   * course's kind order. Undefined when nothing intersects (or the store
   * isn't loaded), which callers render as the bare kindless course URL.
   */
  function firstAccessibleKind(variantKinds: string[] | null | undefined): string | undefined {
    if (!variantKinds?.length) return undefined;
    return variantKinds.find(k => accessibleKinds.value.has(k));
  }

  // The request in flight, shared with every caller that arrives while it runs.
  // On a direct load or refresh the app shell starts the fetch before the page
  // mounts, and a page that awaits ensureLoaded() must wait for that request,
  // not resolve early with no libraries (BPE-770).
  let inFlight: Promise<void> | null = null;

  function fetch(force = false): Promise<void> {
    if (loaded.value && !force) return Promise.resolve();
    if (inFlight) return inFlight;
    inFlight = (async () => {
      loading.value = true;
      error.value = null;
      try {
        libraries.value = await libraryApi.getMine();
        loaded.value = true;
      } catch (e) {
        error.value = e instanceof Error ? e.message : 'Failed to fetch accessible libraries';
      } finally {
        loading.value = false;
        inFlight = null;
      }
    })();
    return inFlight;
  }

  /** Resolves once the libraries are loaded, joining a request already in flight. */
  function ensureLoaded(): Promise<void> {
    return fetch();
  }

  return {
    libraries,
    loading,
    loaded,
    error,
    byId,
    getById,
    accessibleKinds,
    firstAccessibleKind,
    fetch,
    ensureLoaded,
  };
});
